import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useMyReports } from '../hooks/useReports'
import { SPECIES_LABELS, EVENT_TYPE_LABELS } from '../constants/taxonomy'
import type { EventType, SpeciesBucket } from '../types/contact'

const REPORT_STATUS_LABELS: Record<string, string> = {
  pending_fusion: 'Processing',
  fused: 'Included in a contact',
  rejected: 'Rejected by a moderator',
}

const REPORT_STATUS_COLORS: Record<string, string> = {
  pending_fusion: '#64748b',
  fused: '#15803d',
  rejected: '#dc2626',
}

export default function MyReportsPage() {
  const { isAuthenticated } = useAuth()
  const { data, isLoading, isError } = useMyReports()

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: '/my-reports' }} replace />
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          ← Back to map
        </Link>

        <div className="mt-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">My reports</h1>
          <p className="mt-1 text-sm text-gray-500">
            Everything you've submitted, and what happened to it.
          </p>
        </div>

        {isLoading && <p className="text-sm text-gray-500">Loading your reports…</p>}
        {isError && <p className="text-sm text-red-600">Failed to load your reports.</p>}
        {data && data.length === 0 && (
          <p className="text-sm text-gray-500">You haven't submitted any reports yet.</p>
        )}

        <div className="space-y-3">
          {data?.map((r) => (
            <div key={r.id} className="rounded-xl border border-gray-200 bg-white px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900">
                      {SPECIES_LABELS[r.species_bucket as SpeciesBucket] ?? r.species_bucket}
                    </span>
                    <span className="text-xs text-gray-500">
                      {EVENT_TYPE_LABELS[r.event_type as EventType] ?? r.event_type}
                    </span>
                  </div>
                  {r.description && <p className="text-sm text-gray-700 mt-1">{r.description}</p>}
                  <p className="text-xs text-gray-400 mt-1">
                    observed {new Date(r.observed_at).toLocaleString()}
                    {r.photo_count > 0 && ` · ${r.photo_count} photo${r.photo_count === 1 ? '' : 's'}`}
                  </p>
                </div>
                <span
                  className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
                  style={{
                    color: REPORT_STATUS_COLORS[r.status],
                    backgroundColor: REPORT_STATUS_COLORS[r.status] + '1a',
                  }}
                >
                  {REPORT_STATUS_LABELS[r.status] ?? r.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
